/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { useMemo } from "react";
import { format } from "date-fns";
import moment from "moment";
import Expiration from "../../../components/Expiration";
import { TableCell } from "../../../components/DataTable";
import { getDaysDiffBetweenDates } from "../../../utils";

export const VehicleColumns = (editRow, notTerminated, fromVehicle) => {
  const columns = [
    {
      Header: "Vehicle #",
      accessor: "VehicleNumber",
      id: "VehicleNumber",
      sortType: (a, b) => {
        const aVal =
          parseInt(a.original.VehicleNumber, 10) ?? a.original.VehicleNumber;
        const bVal =
          parseInt(b.original.VehicleNumber, 10) ?? b.original.VehicleNumber;

        if (aVal > bVal) return 1;
        if (bVal > aVal) return -1;
        return 0;
      },
      Cell: ({ cell }) => (
        <TableCell.Link
          to={`/fleet/vehicles/${cell?.row?.original?.RecId}/o-o-s-history`}
          value={cell.value}
        />
      )
    },
    {
      Header: "Odometer",
      accessor: "Odometer",
      id: "Odometer"
    },
    {
      Header: "Status",
      accessor: "StatusCodeDescription",
      id: "Status"
    },
    {
      Header: "OOS Comment",
      accessor: "LastOutOfServiceComment",
      id: "LastOutOfServiceComment"
    },
    {
      Header: "OOS Date",
      accessor: "LastOutOfServiceDate",
      id: "LastOutOfServiceDate",
      Cell: ({ cell }) =>
        cell.value ? format(new Date(cell.value), "MM/dd/yyyy") : null
    },
    {
      Header: "Inspection Due",
      accessor: "InspectionDue",
      id: "InspectionDue"
    },
    {
      Header: "Overweight Permit",
      accessor: "OverWeightPermit",
      id: "OverWeightPermit"
    },
    {
      Header: "Overweight Permit Exp.",
      id: "OverWeightPermitExpirationDate",
      Cell: ({ row }) => {
        const expDate = row?.original?.OverWeightPermitExpirationDate;
        if (expDate) {
          const n = Math.floor(
            getDaysDiffBetweenDates(new Date(), new Date(expDate))
          );
          return <Expiration value={n} />;
        }
        return "--";
      }
    },
    {
      Header: "Reg. Exp. Date",
      accessor: "RegistrationExpirationDate",
      id: "RegistrationExpirationDate",
      Cell: ({ cell }) =>
        cell.value ? format(new Date(cell.value), "MM/dd/yyyy") : null
    },
    {
      Header: "Year",
      accessor: "Year",
      id: "Year"
    },
    {
      Header: "Serial #",
      accessor: "SerialNumber",
      id: "SerialNumber"
    },
    {
      Header: "Reg. State",
      accessor: "RegistrationState",
      id: "RegistrationState"
    },
    {
      Header: "Plate #",
      accessor: "PlateNumber",
      id: "PlateNumber"
    },
    {
      Header: "Vehicle Type",
      accessor: "VehicleTypeCodeDescription",
      id: "VehicleTypeCodeDescription"
    },
    {
      Header: "Unladen",
      accessor: "Unladen",
      id: "Unladen"
    },
    {
      Header: "Payload",
      accessor: "Payload",
      id: "Payload"
    },
    {
      Header: "Trycock Net",
      accessor: "TrycockNet",
      id: "TrycockNet"
    },
    {
      Header: "Category",
      accessor: "CategoryCodeDescription",
      id: "Category"
    },
    {
      Header: "Make",
      accessor: "Make",
      id: "Make"
    },
    {
      Header: "Company Owned",
      accessor: "CompanyOwned",
      id: "CompanyOwned"
    },
    {
      Header: "Model",
      accessor: "Model",
      id: "Model"
    },
    {
      Header: "Engine",
      accessor: "Engine",
      id: "Engine"
    },
    {
      Header: "Axles",
      accessor: "Axles",
      id: "Axles"
    },
    {
      Header: "Transmission",
      accessor: "TransmissionCodeDescription",
      id: "Transmission"
    },
    {
      Header: "Rear",
      accessor: "RearCodeDescription",
      id: "Rear"
    },
    {
      Header: "Body",
      accessor: "BodyCodeDescription",
      id: "Body"
    },
    {
      Header: "# of Diesel Tanks",
      accessor: "NumDieselTanks",
      id: "NumDieselTanks"
    },
    {
      Header: "Tire Size",
      accessor: "TireSizeCodeDescription",
      id: "TireSize"
    },
    {
      Header: "Wheel Size",
      accessor: "WheelSizeCodeDescription",
      id: "WheelSize"
    },
    {
      Header: "Max. Cold",
      accessor: "MaxCold",
      id: "MaxCold"
    },
    {
      Header: "Max. Warm",
      accessor: "MaxWarm",
      id: "MaxWarm"
    },
    {
      Header: "Description",
      accessor: "Description",
      id: "Description"
    }
  ];
  if (!notTerminated) {
    columns.splice(0, 0, {
      Header: "Update Status",
      Cell: obj => editRow(obj)
    });
  }

  if (fromVehicle) {
    columns.splice(0, 2);
  }
  return columns;
};

export const VEHICLE_ELD_COLUMNS = [
  {
    Header: "Date",
    accessor: "ELDDate",
    id: "ELDDate",
    Cell: ({ cell }) =>
      cell.value ? format(new Date(cell.value), "MM/dd/yyyy") : null
  },
  {
    Header: "Driver Name",
    accessor: "DriverName",
    id: "DriverName"
  },
  {
    Header: "Odometer",
    accessor: "OdometerReading",
    id: "OdometerReading"
  },
  {
    Header: "Cost",
    accessor: "Cost",
    id: "Cost",
    Cell: ({ cell }) =>
      cell.value !== null ? `$${parseInt(cell.value).toFixed(2)}` : null
  },
  {
    Header: "Type",
    accessor: "Type",
    id: "Type",
    Cell: ({ cell }) => {
      if (cell?.value) {
        return cell.value === "1" ? "Fuel" : "Other";
      }
      return "--";
    }
  },
  {
    Header: "Qty",
    accessor: "Qty",
    id: "Qty"
  }
];

export const VEHICLE_INSPECTIONS_MASTER_COLUMNS = ({
  onSelectAll,
  onSelect,
  selected
}) => [
  {
    Header: ({ data }) => {
      const allSelected = useMemo(
        () =>
          selected.length &&
          data.every(vehicleInspectionMaster =>
            selected.some(
              s => s === vehicleInspectionMaster.InspectionTypeRecIdRef
            )
          ),
        [data]
      );
      const someSelected = useMemo(
        () =>
          selected.length &&
          data.some(vehicleInspectionMaster =>
            selected.some(
              s => s === vehicleInspectionMaster.InspectionTypeRecIdRef
            )
          ),
        [data]
      );
      return (
        <TableCell.Checkbox
          checked={!!allSelected}
          indeterminate={!allSelected && !!someSelected}
          onChange={() =>
            onSelectAll(
              allSelected,
              data.map(
                vehicleInspectionMaster =>
                  vehicleInspectionMaster.InspectionTypeRecIdRef
              )
            )
          }
        />
      );
    },
    id: "RecId",
    defaultCanSort: false,
    disableSortBy: true,
    accessor: "VehicleInspectionsMasters",
    Cell: ({ row }) => (
      <TableCell.Checkbox
        checked={selected.includes(row.original.InspectionTypeRecIdRef)}
        onChange={() => onSelect(row.original)}
        onClick={e => e.stopPropagation()}
      />
    )
  },
  {
    Header: "Inspection Setup",
    accessor: "InspectionType.CodeDescription",
    id: "InspectionType"
  },
  {
    Header: "Mileage Interval",
    accessor: "MileageInterval",
    id: "MileageInterval",
    Cell: ({ cell }) => <TableCell.Number value={JSON.stringify(cell.value)} />
  },
  {
    Header: "Days Interval",
    accessor: "DaysInterval",
    id: "DaysInterval",
    Cell: ({ cell }) => <TableCell.Number value={JSON.stringify(cell.value)} />
  },
  {
    Header: "Active",
    accessor: "Active",
    id: "Active"
  }
];

export const VEHICLE_INSPECTION_COLUMNS = [
  {
    Header: "Inspection History",
    accessor: "InspectionType.CodeDescription",
    id: "InspectionType",
    maxWidth: "20%"
  },
  {
    Header: "WO Completed Date",
    accessor: "WorkOrder.CompletedDate",
    id: "WorkOrder.CompletedDate",
    maxWidth: "10%",
    Cell: ({ cell }) =>
      cell.value
        ? moment(new Date(cell.value).toISOString().slice(0, 10)).format(
            "MM/DD/YYYY"
          )
        : null
  },
  {
    Header: "Work Order",
    accessor: "WorkOrder.WorkOrder",
    Cell: ({ row, cell }) => {
      if (!cell.value) {
        return "--";
      }
      return (
        <TableCell.Link
          to={`/fleet/work-orders/${row.original.WorkOrderRecIdRef}/main-information`}
          value={cell.value}
        />
      );
    }
  },
  {
    Header: "Odometer Reading",
    accessor: "OdometerReading",
    id: "OdometerReading"
  },
  {
    Header: "Inspected By",
    accessor: "MechanicFullName",
    id: "InspectedBy"
  }
];

export const VEHICLE_COLUMS_SEARCH_RES = [
  {
    Header: "Number",
    accessor: "VehicleNumber",
    id: "VehicleNumber",
    sortType: (a, b) => {
      const aVal =
        parseInt(a.original.VehicleNumber, 10) ?? a.original.VehicleNumber;
      const bVal =
        parseInt(b.original.VehicleNumber, 10) ?? b.original.VehicleNumber;

      if (aVal > bVal) return 1;
      if (bVal > aVal) return -1;
      return 0;
    }
  },
  {
    Header: "Description",
    accessor: "Description",
    id: "Description"
  },
  {
    Header: "Type",
    accessor: "VehicleType.CodeDescription",
    id: "VehicleType"
  },
  {
    Header: "Year",
    accessor: "Year",
    id: "Year"
  },
  {
    Header: "Make",
    accessor: "Make",
    id: "Make"
  },
  {
    Header: "Model",
    accessor: "Model",
    id: "Model"
  }
];

export const WORK_ORDER_LABOR_VIEW_COLUMNS = [
  {
    Header: "WO #",
    id: "WONumber",
    accessor: "WorkOrder",
    Cell: ({ cell }) => (
      <TableCell.Link
        to={`/fleet/work-orders/${cell.row.original.WorkOrderRecIdRef}/main-information`}
        value={`#${cell.row.original.WorkOrder}`}
      />
    )
  },
  {
    Header: "Completed Date",
    id: "CompletedDate",
    accessor: "CompletedDate",
    Cell: ({ cell }) =>
      cell.value
        ? moment(new Date(cell.value).toISOString().slice(0, 10)).format(
            "MM/DD/YYYY"
          )
        : null
  },
  {
    Header: "Mechanic",
    accessor: "MechanicName",
    id: "MechanicName"
  },
  {
    Header: "Item Category",
    accessor: "ItemCategoryDescription",
    Cell: ({ cell }) => (cell.value ? cell.value : "-")
  },

  {
    Header: "Description",
    accessor: "Description",
    Cell: ({ cell }) => (cell.value ? cell.value : "-")
  },
  {
    Header: "HRS",
    accessor: "HoursWorked",
    Cell: ({ cell }) => <TableCell.Number value={JSON.stringify(cell.value)} />
  },
  {
    Header: "WO Status",
    accessor: "WorkOrderStatusCodeDescription",
    Cell: ({ cell }) => (cell.value ? cell.value : "-")
  },
  {
    Header: "Note",
    accessor: "LineNote",
    Cell: ({ cell }) => (cell.value ? cell.value : "-")
  },
  {
    Header: "#",
    accessor: "LineNo",
    Cell: ({ cell }) => (cell.value ? cell.value : "-")
  },
  {
    Header: "Created Date",
    accessor: "CreatedDateTime",
    Cell: ({ cell }) =>
      cell.value
        ? moment(new Date(cell.value)).format("MM/DD/YYYY HH:mm")
        : null
  }
];

export const WORK_ORDER_PARTS_VIEW_COLUMNS = Auth => {
  const columns = [
    {
      Header: "WO #",
      id: "WONumber",
      accessor: "WorkOrder",
      Cell: ({ cell }) => (
        <TableCell.Link
          to={`/fleet/work-orders/${cell.row.original.WorkOrderRecIdRef}/main-information`}
          value={`#${cell.row.original.WorkOrder}`}
        />
      )
    },
    {
      Header: "Completed Date",
      id: "CompletedDate",
      accessor: "CompletedDate",
      Cell: ({ cell }) =>
        cell.value
          ? moment(new Date(cell.value).toISOString().slice(0, 10)).format(
              "MM/DD/YYYY"
            )
          : null
    },
    {
      Header: "Mechanic",
      accessor: "MechanicName",
      id: "MechanicName"
    },
    {
      Header: "Item Category",
      accessor: "ItemCategoryDescription",
      Cell: ({ cell }) => (cell.value ? cell.value : "-")
    },
    {
      Header: "Item #",
      accessor: "ItemNo",
      Cell: ({ cell }) => (cell.value ? cell.value : "-")
    },
    {
      Header: "Item Description",
      accessor: "ItemDescription",
      Cell: ({ cell }) => (cell.value ? cell.value : "-")
    },
    {
      Header: "Quantity",
      accessor: "Quantity",
      id: "Quantity",
      Cell: ({ cell }) => (
        <TableCell.Number value={JSON.stringify(cell.value)} />
      )
    }
  ];
  if (Auth) {
    columns.push({
      Header: "Cost",
      accessor: "Cost",
      Cell: ({ cell }) => (
        <TableCell.Currency value={JSON.stringify(cell.value)} />
      )
    });
  }
  return [
    ...columns,
    ...[
      {
        Header: "Odometer",
        accessor: "Odometer",
        Cell: ({ cell }) => (
          <TableCell.Number value={JSON.stringify(cell.value)} />
        )
      },
      {
        Header: "WO Status",
        accessor: "WorkOrderStatusCodeDescription",
        Cell: ({ cell }) => (cell.value ? cell.value : "-")
      },
      {
        Header: "Note",
        accessor: "LineNote",
        Cell: ({ cell }) => (cell.value ? cell.value : "-")
      },
      {
        Header: "#",
        accessor: "LineNo",
        Cell: ({ cell }) => (cell.value ? cell.value : "-")
      }
    ]
  ];
};
